import { IDocumentFileModel, IDocumentFolderModel, IDocumentFolderWithAccess, IExternalLinkModel } from '@api/models';
import { NavTabPills, useTab } from '@components/tabs';
import useAuthSelector from '@hooks/use-auth-selector';
import useDialog from '@hooks/use-dialog';

import DocumentList from 'modules/documents/components/DocumentList/DocumentList';
import useDocumentListData from 'modules/documents/hooks/useDocumentListData';
// import { useDocumentHierarchy } from 'modules/shared/features/document-hierarchy';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { ScrollRestoration, useParams, useSearchParams } from 'react-router-dom';
import AddDocumentDrawer from 'modules/documents/components/AddDocumentDrawer/AddDocumentDrawer';
import DeleteDocumentDrawer from 'modules/documents/components/DeleteDocumentDrawer/DeleteDocumentDrawer';
import EditFolderDrawer from 'modules/documents/components/EditFolderDrawer/EditFolderDrawer';
import CreateFolderDrawer from 'modules/documents/components/CreateFolderDrawer/CreateFolderDrawer';
import DeleteFolderDrawer from 'modules/documents/components/DeleteFolderDrawer/DeleteFolderDrawer';
import DocumentAccessDrawer from 'modules/documents/components/DocumentAccessDrawer/DocumentAccessDrawer';
import FolderAccessDrawer from 'modules/documents/components/FolderAccessDrawer/FolderAccessDrawer';
import useUserListData from 'modules/users/hooks/useUserListData';
import ExternalLinkList from 'modules/documents/components/ExternalLinkList/ExternalLinkList';
import AddExternalLinkDrawer from 'modules/documents/components/AddExternalLinkDrawer/AddExternalLinkDrawer';
import ExternalLinkDrawer from 'modules/documents/components/ExternalLinkDrawer/ExternalLinkDrawer';
import ExternalLinkAccessDrawer from 'modules/documents/components/ExternalLinkAccessDrawer/ExternalLinkAccessDrawer';
import DeleteExternalLinkDrawer from 'modules/documents/components/DeleteExternalLinkDrawer/DeleteExternalLinkDrawer';
import { CircularProgress } from '@hyperflake/react-ui-library';
import { useDocumentHierarchyWithoutFilter } from 'modules/shared/features/document-hierarchy';
import MoveFolderDrawer from 'modules/documents/components/MoveFolderDrawer/MoveFolderDrawer';
import MoveFileDrawer from 'modules/documents/components/MoveFileDrawer/MoveFileDrawer';
import RenameFileDrawer from 'modules/documents/components/RenameFileDrawer/RenameFileDrawer';
import MoveExternalLinkDrawer from 'modules/documents/components/MoveExternalLinkDrawer/MoveExternalLinkDrawer';
import RenameExternalFileDrawer from 'modules/documents/components/RenameExternalFileDrawer/RenameExternalFileDrawer';

const useFolderState = () => {
    const queryName = 'f';

    const [searchParams, setSearchParams] = useSearchParams();

    const folderId = searchParams.get(queryName) || null;

    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId);

    useEffect(() => {
        if (selectedFolderId) {
            searchParams.set(queryName, selectedFolderId);
        } else {
            searchParams.delete(queryName);
        }

        setSearchParams(searchParams, {
            replace: true,
        });
    }, [selectedFolderId]);

    return [selectedFolderId, setSelectedFolderId] as const;
};

enum ActiveTabEnum {
    DOCUMENT = 'documents',
    EXTERNAL_LINK = 'external_link',
}

const DocumentListPageWithFolders = () => {
    const { clientId } = useParams();
    const [activeTab, setActiveTab] = useTab(ActiveTabEnum.DOCUMENT);
    const [searchParams] = useSearchParams();
    const selectedTab = searchParams.get('tab');

    const [selectedFolderId, setSelectedFolderId] = useFolderState();

    const addFolderDialog = useDialog<{ parentFolderId: string | null }>();
    const editFolderDialog = useDialog<IDocumentFolderModel>();
    const deleteFolderDialog = useDialog<IDocumentFolderModel>();
    const moveFolderDialog = useDialog<IDocumentFolderModel>();

    const uploadFileDialog = useDialog();
    const userAccessDialog = useDialog<IDocumentFileModel>();
    const userFolderAccessDialog = useDialog<IDocumentFolderWithAccess>();
    const deleteFileDialog = useDialog<IDocumentFileModel>();
    const moveFileDialog = useDialog<IDocumentFileModel>();
    const renameFileDialog = useDialog<IDocumentFileModel>();

    const uploadExternalLinkDialog = useDialog();
    const userExternalLinkAccessDialog = useDialog<IExternalLinkModel>();
    const externalLinkDialog = useDialog<IExternalLinkModel>();
    const deleteExternalLinkDialog = useDialog<IExternalLinkModel>();
    const renameExternalFileDialog = useDialog<IExternalLinkModel>();
    const moveExternalLinkDialog = useDialog<IExternalLinkModel>();

    const documentListData = useDocumentListData(clientId);
    // const userListQuery = useLeanUserListQuery();
    const userListQuery = useUserListData(clientId);

    const documentHierarchy = useDocumentHierarchyWithoutFilter({
        folders: documentListData.folders,
        files: documentListData.files,
        externalLinks: documentListData.externalLinks,
    });

    const breadcrumb = documentHierarchy.getFolderBreadCrumb(selectedFolderId);

    const folders = useMemo(() => {
        const folders = documentHierarchy.getChildren(selectedFolderId || 'root')?.folders;

        return folders;
    }, [documentHierarchy, selectedFolderId]);

    const files = useMemo(() => {
        return documentHierarchy.getChildren(selectedFolderId || 'root')?.files;
    }, [documentHierarchy, selectedFolderId]);

    const externalLinks = useMemo(() => {
        return documentHierarchy.getChildren(selectedFolderId || 'root')?.externalLinks;
    }, [documentHierarchy, selectedFolderId]);

    // When we change Tab using Search
    useEffect(() => {
        if (selectedTab === ActiveTabEnum.DOCUMENT) {
            setActiveTab(ActiveTabEnum.DOCUMENT);
        } else if (selectedTab === ActiveTabEnum.EXTERNAL_LINK) {
            setActiveTab(ActiveTabEnum.EXTERNAL_LINK);
        }
    }, [selectedTab, setActiveTab]);

    const handleDocumentAccessUpdate = useCallback(
        (fileId: string, updatedFile: IDocumentFileModel) => {
            // documentListData.updateFile(fileId, updatedFile);
            documentListData.refetchAll();

            userAccessDialog.setData(updatedFile);
        },
        [documentListData, userAccessDialog]
    );
    const handleFolderAccessUpdate = useCallback(
        (folderId: string, updatedFolder: IDocumentFolderWithAccess) => {
            // documentListData.updateFolder(folderId, updatedFolder);
            documentListData.refetchAll();

            userFolderAccessDialog.setData(updatedFolder);
        },
        [documentListData, userAccessDialog]
    );

    const handleExternalLinkAccessUpdate = useCallback(
        (externalLinkId: string, updatedExternalLink: IExternalLinkModel) => {
            // documentListData.updateExternalLink(externalLinkId, updatedExternalLink);
            documentListData.refetchAll();
            userExternalLinkAccessDialog.setData(updatedExternalLink);
        },
        [documentListData, userExternalLinkAccessDialog]
    );

    const handleCreateFolder = useCallback(() => {
        addFolderDialog.show({ parentFolderId: selectedFolderId });
    }, [addFolderDialog, selectedFolderId]);

    if (documentListData.isLoading)
        return (
            <div className="fixed inset-0 flex items-center justify-center">
                <CircularProgress />
            </div>
        );

    // const showEditButton =
    //     (hasPermission(PermissionEnum.DOCUMENT_UPDATE) ||
    //         (selectedCategory?.createdBy as UserModel)?._id === user._id) &&
    //     selectedCategoryId;

    // const showDeleteButton =
    //     (hasPermission(PermissionEnum.DOCUMENT_DELETE) ||
    //         (selectedCategory?.createdBy as UserModel)?._id === user._id) &&
    //     selectedCategoryId;

    return (
        <>
            <ScrollRestoration />

            <section className="px-6 ">
                <header className="flex items-center gap-6">
                    {/* {hasProPlan && (
                        <div className="ml-auto flex items-center gap-4">
                            {showEditButton && (
                                <IconButton color="secondary" onClick={editCategoryDialog.show}>
                                    <PenToSquareIcon width="18" height="18" className="text-grayscale-600" />
                                </IconButton>
                            )}

                            {showDeleteButton && (
                                <IconButton color="secondary" onClick={deleteCategoryDialog.show}>
                                    <TrashIcon width="20" height="20" className="text-grayscale-600" />
                                </IconButton>
                            )}

                            <DesktopMediaQuery>
                                <SelectDropdown
                                    className="btn-secondary btn-secondary-hover btn-dropdown"
                                    value={selectedCategoryId}
                                    onChange={(val) => setSelectedCategoryId(val as string)}
                                    options={documentListData.categoryOptions}
                                    render={(label) => (
                                        <span className="flex items-center gap-2 text-grayscale-500 font-medium">
                                            Category:<span className="text-grayscale-900 font-semibold">{label}</span>
                                            <span>
                                                <ChevronDownIcon className="text-grayscale-400" strokeWidth="2" />
                                            </span>
                                        </span>
                                    )}
                                />

                                <Button color="secondary" onClick={addCategoryDialog.show}>
                                    <PlusIcon width="10" height="10" /> Add category
                                </Button>
                            </DesktopMediaQuery>
                        </div>
                    )} */}
                </header>

                <nav className="mt-3">
                    <NavTabPills
                        items={[
                            {
                                content: `Documents (${documentListData.files.length})`,
                                value: ActiveTabEnum.DOCUMENT,
                            },
                            {
                                content: `External Links (${documentListData.externalLinks.length})`,
                                value: ActiveTabEnum.EXTERNAL_LINK,
                            },
                        ]}
                        value={activeTab}
                        onChange={(val) => {
                            setActiveTab(val);
                        }}
                    />
                </nav>

                <main className="mt-6">
                    {activeTab === ActiveTabEnum.DOCUMENT && (
                        <DocumentList
                            breadcrumb={breadcrumb}
                            folders={folders}
                            files={files}
                            onFolderClick={setSelectedFolderId}
                            onFolderEdit={editFolderDialog.show}
                            onFolderDelete={deleteFolderDialog.show}
                            onMoveFolderClick={moveFolderDialog.show}
                            onUserAccessClick={userAccessDialog.show}
                            onDeleteClick={deleteFileDialog.show}
                            onUploadFileClick={uploadFileDialog.show}
                            onMoveFileClick={moveFileDialog.show}
                            onRenameFileClick={renameFileDialog.show}
                            onAddFolderClick={handleCreateFolder}
                            onUserFolderAccessClick={userFolderAccessDialog.show}
                        />
                    )}

                    {activeTab === ActiveTabEnum.EXTERNAL_LINK && (
                        <ExternalLinkList
                            breadcrumb={breadcrumb}
                            folders={folders}
                            externalLinks={externalLinks}
                            onFolderClick={setSelectedFolderId}
                            onFolderEdit={editFolderDialog.show}
                            onFolderDelete={deleteFolderDialog.show}
                            onUserAccessClick={userExternalLinkAccessDialog.show}
                            onMoveExternalLinkClick={moveExternalLinkDialog.show}
                            onMoveFolderClick={moveFolderDialog.show}
                            onExternalLinkClick={externalLinkDialog.show}
                            onRenameExternalLinkClick={renameExternalFileDialog.show}
                            onDeleteClick={deleteExternalLinkDialog.show}
                            onAddExternalLinkClick={uploadExternalLinkDialog.show}
                            onAddFolderClick={handleCreateFolder}
                        />
                    )}
                </main>
            </section>

            {/* Folder Drawers */}

            <DeleteFolderDrawer
                clientId={clientId}
                show={deleteFolderDialog.isOpen}
                folder={deleteFolderDialog.data}
                onClose={deleteFolderDialog.hide}
                onDelete={documentListData.deleteFolder}
            />
            <CreateFolderDrawer
                clientId={clientId}
                show={addFolderDialog.isOpen}
                onClose={addFolderDialog.hide}
                onAdd={documentListData.addFolder}
                parentFolderId={addFolderDialog.data?.parentFolderId}
            />
            <EditFolderDrawer
                clientId={clientId}
                folder={editFolderDialog.data}
                show={editFolderDialog.isOpen}
                onClose={editFolderDialog.hide}
                onUpdate={documentListData.updateFolder}
            />
            <MoveFolderDrawer
                clientId={clientId}
                show={moveFolderDialog.isOpen}
                folders={documentListData.folders}
                selectedFolder={moveFolderDialog.data}
                onClose={moveFolderDialog.hide}
                refetchAll={documentListData.refetchAll}
            />

            {/* Document Drawers */}
            <AddDocumentDrawer
                clientId={clientId}
                show={uploadFileDialog.isOpen}
                onClose={uploadFileDialog.hide}
                selectedFolderId={selectedFolderId}
                onAdd={documentListData.addFile}
            />
            <DeleteDocumentDrawer
                show={deleteFileDialog.isOpen}
                onClose={deleteFileDialog.hide}
                file={deleteFileDialog.data}
                onDelete={documentListData.deleteFile}
            />
            <DocumentAccessDrawer
                clientId={clientId}
                selectedFolderId={selectedFolderId}
                show={userAccessDialog.isOpen}
                onClose={userAccessDialog.hide}
                file={userAccessDialog.data}
                onAccessUpdate={handleDocumentAccessUpdate}
                users={userListQuery.isLoading ? [] : userListQuery.data}
            />
            <FolderAccessDrawer
                clientId={clientId}
                selectedFolderId={selectedFolderId}
                show={userFolderAccessDialog.isOpen}
                onClose={userFolderAccessDialog.hide}
                folder={userFolderAccessDialog.data}
                onAccessUpdate={handleFolderAccessUpdate}
                users={userListQuery.isLoading ? [] : userListQuery.data}
            />

            <MoveFileDrawer
                clientId={clientId}
                show={moveFileDialog.isOpen}
                folders={documentListData.folders}
                selectedFile={moveFileDialog.data}
                onClose={moveFileDialog.hide}
                refetchAll={documentListData.refetchAll}
            />
            {renameFileDialog.isOpen && (
                <RenameFileDrawer
                    clientId={clientId}
                    show={renameFileDialog.isOpen}
                    file={renameFileDialog.data}
                    onClose={renameFileDialog.hide}
                    refetchAll={documentListData.refetchAll}
                />
            )}

            {/* External Link Drawers */}

            <AddExternalLinkDrawer
                clientId={clientId}
                show={uploadExternalLinkDialog.isOpen}
                onClose={uploadExternalLinkDialog.hide}
                selectedFolderId={selectedFolderId}
                onAdd={documentListData.addExternalLink}
            />

            <ExternalLinkDrawer
                show={externalLinkDialog.isOpen}
                onClose={externalLinkDialog.hide}
                externalLink={externalLinkDialog.data}
            />

            <ExternalLinkAccessDrawer
                clientId={clientId}
                selectedFolderId={selectedFolderId}
                show={userExternalLinkAccessDialog.isOpen}
                onClose={userExternalLinkAccessDialog.hide}
                externalLink={userExternalLinkAccessDialog.data}
                onAccessUpdate={handleExternalLinkAccessUpdate}
                users={userListQuery.isLoading ? [] : userListQuery.data}
            />

            <RenameExternalFileDrawer
                clientId={clientId}
                show={renameExternalFileDialog.isOpen}
                selectedExternalLink={renameExternalFileDialog.data}
                onClose={renameExternalFileDialog.hide}
                refetchAll={documentListData.refetchAll}
            />
            <DeleteExternalLinkDrawer
                clientId={clientId}
                show={deleteExternalLinkDialog.isOpen}
                onClose={deleteExternalLinkDialog.hide}
                externalLink={deleteExternalLinkDialog.data}
                onDelete={documentListData.deleteExternalLink}
            />

            <MoveExternalLinkDrawer
                clientId={clientId}
                show={moveExternalLinkDialog.isOpen}
                folders={documentListData.folders}
                selectedExternalLink={moveExternalLinkDialog.data}
                onClose={moveExternalLinkDialog.hide}
                refetchAll={documentListData.refetchAll}
            />
        </>
    );
};

export default DocumentListPageWithFolders;
